import React, { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "components/AppIcon";
import { useAuth } from "../../contexts/AuthContext";

export default function ForgotPassword() {
  const { sendPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email) { setError("Email is required"); return; }
    if (!/\S+@\S+\.\S+/?.test(email)) { setError("Enter a valid email address"); return; }
    setError("");
    setIsLoading(true);

    const { error: resetError } = await sendPasswordReset(email);
    setIsLoading(false);

    if (resetError) {
      setError(resetError?.message || "Failed to send reset email. Please try again.");
      return;
    }

    setIsSuccess(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-teal-700/10 rounded-full blur-3xl" />
      </div>
      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/landing-page" className="inline-flex items-center gap-3 group">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-200">
              <Icon name="Zap" size={24} color="#FFFFFF" strokeWidth={2.5} />
            </div>
            <span className="text-2xl font-bold text-white font-heading">
              Fit<span className="text-teal-400">Pulse</span>
            </span>
          </Link>
        </div>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {isSuccess ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-5">
                <Icon name="MailCheck" size={32} color="#10b981" />
              </div>
              <h2 className="text-xl font-bold text-white font-heading mb-2">Check your inbox</h2>
              <p className="text-slate-400 text-sm font-body mb-2">
                We've sent a password reset link to
              </p>
              <p className="text-teal-400 font-medium text-sm font-caption mb-6">{email}</p>
              <p className="text-slate-500 text-xs font-body mb-6">
                Didn't receive it? Check your spam folder or try again in a few minutes.
              </p>
              <button
                onClick={() => { setIsSuccess(false); setEmail(""); }}
                className="text-sm text-teal-400 hover:text-teal-300 transition-colors font-caption"
              >
                Try a different email
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mb-4">
                  <Icon name="KeyRound" size={22} color="#2dd4bf" />
                </div>
                <h1 className="text-2xl font-bold text-white font-heading">Reset your password</h1>
                <p className="text-slate-400 text-sm mt-1 font-body">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-300 font-caption">Email address</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                      <Icon name="Mail" size={16} />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e?.target?.value); setError(""); }}
                      placeholder="you@gymname.com"
                      className={`w-full h-11 pl-10 pr-4 rounded-xl bg-white/5 border text-white placeholder-slate-500 text-sm font-body focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all ${
                        error ? "border-red-500/60" : "border-white/10 focus:border-teal-500/50"
                      }`}
                    />
                  </div>
                  {error && <p className="text-xs text-red-400 font-caption">{error}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold text-sm font-caption transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg shadow-teal-900/30"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Reset Link
                      <Icon name="Send" size={16} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 text-center">
            <Link to="/login-page" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors font-caption">
              <Icon name="ArrowLeft" size={14} />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
